import { usePortal } from "../HubPortalContext";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowRight, Share2, Download, Copy, Check,
  QrCode, RefreshCw, Signal, Mail
} from "lucide-react";

const ROLE_CONFIG = {
  planner: { icon: require("lucide-react").LayoutDashboard, label: "Planner", color: "bg-lime-500", textColor: "text-slate-950" },
  driver: { icon: require("lucide-react").Truck, label: "Driver", color: "bg-blue-500", textColor: "text-white" },
  machinist: { icon: require("lucide-react").Wrench, label: "Machinist", color: "bg-amber-500", textColor: "text-slate-950" },
  service: { icon: require("lucide-react").Droplets, label: "Service", color: "bg-violet-500", textColor: "text-white" },
  transport: { icon: require("lucide-react").Navigation, label: "Transport", color: "bg-teal-500", textColor: "text-white" },
  viewer: { icon: require("lucide-react").Eye, label: "Viewer", color: "bg-slate-400", textColor: "text-white" },
};

export default function HubLobbyView() {
  const ctx = usePortal();
  const {
    t, activeHub, hubStatus, invites, inviteRole, setInviteRole,
    inviteName, setInviteName, inviteEmail, setInviteEmail, handleCreateInvite, expandedQR, setExpandedQR,
    shareInvite, downloadQR, copyInviteLink, copied,
    handleEnterHub, myHubs, loadHubStatus,
  } = ctx;

  if (!activeHub) return null;

  return (
    <div className="w-full max-w-2xl" data-testid="hub-lobby-view">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-black tracking-tighter uppercase text-slate-900 mb-1">{activeHub.name}</h2>
        <div className="flex items-center justify-center gap-2">
          <span className={`text-xs font-bold px-3 py-1 rounded-full ${hubStatus?.is_deployed ? "bg-lime-100 text-lime-600" : "bg-amber-100 text-amber-400"}`}>
            {hubStatus?.is_deployed ? t("deployed_status").toUpperCase() : t("waiting_for_crew").toUpperCase()}
          </span>
        </div>
      </div>

      {/* Occupancy grid */}
      {(() => {
        const rc2 = hubStatus?.role_config || "full";
        // In 2+1 setups, all 3 core roles are required (planner fills co-role automatically)
        const requiredRoles = rc2 === "solo" ? ["planner"] : ["planner", "driver", "machinist"];
        const coRole = hubStatus?.co_role || null; // e.g. "machinist" for planner_driver
        const optionalRoles = ["planner", "driver", "machinist", "service", "transport", "viewer"].filter(r => !requiredRoles.includes(r));
        return (
          <>
            <div className={`grid gap-3 mb-4 ${requiredRoles.length === 1 ? "grid-cols-1 max-w-xs mx-auto" : requiredRoles.length === 2 ? "grid-cols-2" : "grid-cols-3"}`} data-testid="occupancy-grid">
              {requiredRoles.map((role) => {
                const rc = ROLE_CONFIG[role];
                const Icon = rc.icon;
                const count = hubStatus?.occupancy?.[role] || 0;
                const filled = count > 0;
                return (
                  <div key={role} className={`p-5 rounded-xl border text-center transition-all ${filled ? "bg-slate-50 border-lime-400 shadow-sm" : "bg-slate-50/80 border-slate-200 border-dashed"}`} data-testid={`occupancy-${role}`}>
                    <div className={`w-12 h-12 ${filled ? rc.color : "bg-slate-100"} rounded-xl flex items-center justify-center mx-auto mb-3 ${filled ? rc.textColor : "text-slate-400"}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <p className="text-sm font-bold text-slate-900 uppercase tracking-tight">{t("role_" + role) || rc.label}</p>
                    {coRole === role && filled ? (
                      <p className="text-[10px] font-bold mt-1 text-amber-600 uppercase tracking-wide">{t("filled_by_planner") || "Filled by Planner"}</p>
                    ) : (
                      <p className={`text-xs font-bold mt-1 ${filled ? "text-lime-600" : "text-slate-400"}`}>{filled ? `${count} ${t("online_status")}` : t("empty_status")}</p>
                    )}
                  </div>
                );
              })}
            </div>
            {optionalRoles.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-8" data-testid="optional-roles-grid">
                {optionalRoles.map((role) => {
                  const rc = ROLE_CONFIG[role];
                  const Icon = rc.icon;
                  const count = hubStatus?.occupancy?.[role] || 0;
                  const filled = count > 0;
                  return (
                    <div key={role} className={`p-3 rounded-xl border text-center transition-all ${filled ? "bg-slate-50 border-slate-200" : "bg-slate-50/50 border-slate-200 border-dashed"}`} data-testid={`occupancy-${role}`}>
                      <div className={`w-8 h-8 ${filled ? rc.color : "bg-slate-100"} rounded-xl flex items-center justify-center mx-auto mb-2 ${filled ? rc.textColor : "text-slate-400"}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">{t("role_" + role) || rc.label}</p>
                      <p className={`text-[9px] font-bold mt-0.5 ${filled ? "text-lime-600" : "text-slate-400"}`}>{filled ? `${count}` : t("optional_status")}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        );
      })()}

      {/* Progress bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{t("hub_occupancy")}</span>
          <span className="text-sm font-black text-lime-600">{hubStatus?.filled_roles || 0}/{hubStatus?.min_occupancy || 3}</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-lime-500 rounded-full transition-all duration-500"
            style={{ width: `${((hubStatus?.filled_roles || 0) / (hubStatus?.min_occupancy || 3)) * 100}%` }} />
        </div>
      </div>

      {/* Members */}
      {hubStatus?.members?.length > 0 && (
        <div className="mb-8">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">{t("active_crew")}</h3>
          <div className="grid gap-2">
            {hubStatus.members.map((m) => {
              const rc = ROLE_CONFIG[m.role] || ROLE_CONFIG.viewer;
              return (
                <div key={m.id} className="flex items-center gap-3 p-3 bg-slate-50/80 border border-slate-200 rounded-xl">
                  <div className={`w-8 h-8 ${rc.color} rounded-xl flex items-center justify-center ${rc.textColor} text-xs font-black`}>
                    {m.display_name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-800">{m.display_name}</p>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{t("role_" + m.role) || rc.label}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Invite Section */}
      <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl mb-6" data-testid="invite-section">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">{t("invite_crew")}</h3>
        {(() => {
          const rc2 = hubStatus?.role_config || "full";
          // In 2+1 setups, planner fills the co-role automatically; only the +1 needs to be invited
          const coRole2 = hubStatus?.co_role || null;
          const allRequired = rc2 === "solo" ? ["planner"] : ["planner", "driver", "machinist"];
          const occ = hubStatus?.occupancy || {};
          // Filter out roles already filled (including co-role filled by planner)
          const neededRoles = allRequired.filter(r => r !== "planner" && !(occ[r] > 0));
          const optionalInviteRoles = ["driver", "machinist", "service", "transport", "viewer"].filter(r => !neededRoles.includes(r) && r !== coRole2);
          return (
            <>
              {neededRoles.length > 0 && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <p className="text-[11px] font-bold text-amber-600">
                    {t("needed_to_deploy")}: {neededRoles.map(r => t("role_" + r) || ROLE_CONFIG[r]?.label || r).join(", ")}
                  </p>
                </div>
              )}
              <div className="flex gap-2 mb-3 flex-wrap">
                {[...neededRoles, ...optionalInviteRoles].map((r) => {
                  const isNeeded = neededRoles.includes(r);
                  return (
                    <button key={r} data-testid={`invite-role-${r}`} onClick={() => setInviteRole(r)}
                      className={`flex-1 min-w-[70px] py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-wider transition-all relative ${
                        inviteRole === r
                          ? isNeeded ? "bg-amber-100 text-amber-600 border-2 border-amber-400 shadow-sm" : "bg-lime-100 text-lime-600 border-2 border-lime-400"
                          : isNeeded ? "bg-amber-50 text-amber-500 border border-amber-300 hover:border-amber-400" : "bg-slate-100 text-slate-400 border border-slate-200 hover:border-slate-300"
                      }`}>
                      {t("role_" + r) || ROLE_CONFIG[r]?.label || r}
                      {isNeeded && <span className="absolute -top-1.5 -right-1.5 w-2.5 h-2.5 bg-amber-400 rounded-full animate-pulse" />}
                    </button>
                  );
                })}
              </div>
            </>
          );
        })()}
        <div className="space-y-2">
          <div className="flex gap-2">
            <input data-testid="invite-name-input" value={inviteName} onChange={(e) => setInviteName(e.target.value)}
              placeholder={t("display_name_opt")}
              className="flex-1 p-3 bg-white border border-slate-200 rounded-xl text-slate-900 text-sm font-medium placeholder:text-slate-400 focus:border-lime-500 outline-none" />
            <button data-testid="create-invite-btn" onClick={handleCreateInvite}
              className="px-5 py-3 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-xs font-black uppercase">
              {t("generate_link")}
            </button>
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input data-testid="invite-email-input" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={t("invite_email_optional") || "Email (optional - sends invite automatically)"}
              className="w-full pl-10 pr-4 p-3 bg-white border border-slate-200 rounded-xl text-slate-900 text-sm font-medium placeholder:text-slate-400 focus:border-lime-500 outline-none" />
          </div>
        </div>

        {invites.length > 0 && (
          <div className="mt-4 space-y-3 max-h-[500px] overflow-y-auto">
            {invites.map((inv) => {
              const link = `${window.location.origin}/join/${inv.token}`;
              const rc = ROLE_CONFIG[inv.role] || ROLE_CONFIG.viewer;
              const isExpanded = expandedQR === inv.token;
              return (
                <div key={inv.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`invite-card-${inv.token}`}>
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 ${rc.color} rounded-lg flex items-center justify-center`}>
                        {(() => { const Icon = rc.icon; return <Icon className={`w-3 h-3 ${rc.textColor}`} />; })()}
                      </div>
                      <span className="text-xs font-bold text-slate-700 uppercase">{t("role_" + inv.role) || rc.label}</span>
                      {inv.display_name && <span className="text-xs text-slate-400">({inv.display_name})</span>}
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${inv.used ? "bg-slate-200 text-slate-500" : "bg-lime-100 text-lime-600"}`}>
                        {inv.used ? "USED" : "ACTIVE"}
                      </span>
                    </div>
                    {!inv.used && (
                      <button onClick={() => setExpandedQR(isExpanded ? null : inv.token)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${isExpanded ? "bg-lime-100 text-lime-600 border border-lime-300" : "bg-slate-100 hover:bg-slate-200 text-slate-500 border border-transparent"}`}
                        data-testid={`qr-toggle-${inv.token}`}>
                        <QrCode className="w-3.5 h-3.5" />
                        {isExpanded ? t("hide_qr") : "QR Code"}
                      </button>
                    )}
                  </div>

                  {!inv.used && isExpanded && (
                    <div className="px-3 pb-4 animate-fadeIn">
                      <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col items-center" data-testid={`qr-panel-${inv.token}`}>
                        <QRCodeSVG id={`qr-svg-${inv.token}`} value={link} size={200} level="M" includeMargin={false} bgColor="#ffffff" fgColor="#0f172a" />
                        <p className="text-xs font-bold text-slate-700 mt-3 text-center">{activeHub?.name}</p>
                        <p className="text-[10px] text-slate-400 text-center">{t("enter_as")} {t("role_" + inv.role) || rc.label}</p>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-3">
                        <button onClick={() => shareInvite(inv.token)} className="flex items-center justify-center gap-1.5 py-2.5 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-xs font-black uppercase active:scale-[0.97] transition-all" data-testid={`share-invite-${inv.token}`}>
                          <Share2 className="w-3.5 h-3.5" /> {t("share")}
                        </button>
                        <button onClick={() => downloadQR(inv.token)} className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold uppercase border border-slate-200 active:scale-[0.97] transition-all" data-testid={`download-qr-${inv.token}`}>
                          <Download className="w-3.5 h-3.5" /> {t("save")}
                        </button>
                        <button onClick={() => copyInviteLink(inv.token)} className="flex items-center justify-center gap-1.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs font-bold uppercase border border-slate-200 active:scale-[0.97] transition-all" data-testid={`copy-invite-${inv.token}`}>
                          {copied === inv.token ? <Check className="w-3.5 h-3.5 text-lime-600" /> : <Copy className="w-3.5 h-3.5" />}
                          {copied === inv.token ? t("copied") : t("copy")}
                        </button>
                      </div>
                    </div>
                  )}

                  {!inv.used && !isExpanded && (
                    <div className="px-3 pb-3 flex gap-2">
                      <input id={`invite-link-${inv.token}`} readOnly value={link}
                        onClick={e => { e.target.select(); copyInviteLink(inv.token); }}
                        className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-mono text-blue-600 cursor-pointer select-all focus:border-blue-400 outline-none"
                        data-testid={`invite-link-${inv.token}`} />
                      <button onClick={() => shareInvite(inv.token)}
                        className="px-3 py-2 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-lg text-xs font-bold flex items-center gap-1"
                        data-testid={`share-quick-${inv.token}`}>
                        <Share2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Enter / Deploy button */}
      {hubStatus?.is_deployed ? (
        <button data-testid="enter-hub-btn"
          onClick={() => {
            const myMembership = myHubs.find(h => h.hub_id === activeHub.id) || hubStatus?.members?.[0];
            if (myMembership) handleEnterHub({ ...myMembership, hub_name: activeHub.name });
          }}
          className="w-full py-5 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 shadow-md">
          {t("enter_hub")} <ArrowRight className="w-5 h-5" />
        </button>
      ) : (
        <div className="text-center p-4 bg-slate-50/80 border border-amber-300 rounded-xl">
          <Signal className="w-6 h-6 text-amber-400 mx-auto mb-2 animate-pulse" />
          <p className="text-sm text-amber-400 font-bold">{t("waiting_min_crew")} ({hubStatus?.filled_roles || 0}/{hubStatus?.min_occupancy || 3})</p>
          <p className="text-xs text-slate-400 mt-1">
            {(() => {
              const rc2 = hubStatus?.role_config || "full";
              if (rc2 === "solo") return t("hub_deploys_solo");
              if (rc2 === "planner_driver") return t("hub_deploys_pd");
              if (rc2 === "planner_machinist") return t("hub_deploys_pm");
              return t("hub_deploys_full");
            })()}
          </p>
          <button onClick={() => loadHubStatus(activeHub.id)}
            className="mt-3 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-400 rounded-xl text-xs font-bold flex items-center gap-2 mx-auto"
            data-testid="refresh-status-btn">
            <RefreshCw className="w-3.5 h-3.5" /> {t("refresh_status")}
          </button>
        </div>
      )}
    </div>
  );
}
